import algoliasearch from 'algoliasearch';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import React, { memo, useContext } from 'react';
import {
  Configure,
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch-hooks-web';
import { LayoutContext } from '../../contexts/LayoutContext';
import { PageContext } from '../../contexts/PageContext';
import { Blok } from '../../interfaces/Blok.interface';
import { StoryblokArticleAuthor } from '../../interfaces/StoryblokArticleAuthor.interface';
import { StoryblokNode } from '../../interfaces/StoryblokNode.interface';
import { Theme } from '../../interfaces/enums/Theme.enum';
import {
  SearchIndexName,
  getInitialPage,
  getInitialQuery,
} from '../../utils/getAlgoliaSearchConfig/getAlgoliaSearchConfig';
import { getHeroBloksFromBody } from '../../utils/getHeroBloksFromBody/getHeroBloksFromBody.util';
import { getNonHeroBloksFromBody } from '../../utils/getNonHeroBloksFromBody/getNonHeroBloksFromBody.util';
import BreadcrumbBar from '../BreadcrumbBar/BreadcrumbBar';
import { FlatArticleIndexPageResultsBlokProps } from '../FlatArticleIndexPageResults/FlatArticleIndexPageResults';
import { GroupedArticleIndexPageResultsBlokProps } from '../GroupedArticleIndexPageResults/GroupedArticleIndexPageResults';
import TestingGrid from '../TestingGrid/TestingGrid.molecule';
import { AuthorContext } from '../../contexts/AuthorContext';

interface AuthorPageBlokProps extends Blok {
  body?: Blok[] | null;
  show_share_page_bar: boolean;
  show_contact_bar: boolean;
  breadcrumb_theme?: Theme;
  author: StoryblokNode<StoryblokArticleAuthor>;
  searchable_category_tags?: string[];
  results?:
    | GroupedArticleIndexPageResultsBlokProps[]
    | FlatArticleIndexPageResultsBlokProps[];
}

interface AuthorPageProps {
  blok: AuthorPageBlokProps;
}

const AuthorPage: React.FC<AuthorPageProps> = ({ blok }) => {
  const { header, footer, contact_bar, share_page_bar, breadcrumbs } =
    useContext(LayoutContext);
  const heroBloks: Blok[] = getHeroBloksFromBody(blok.body || []);
  const nonHeroBloks: Blok[] = getNonHeroBloksFromBody(blok.body || []);

  const {
    body = [],
    show_contact_bar,
    show_share_page_bar,
    breadcrumb_theme,
    author,
  } = blok;

  const filters = () => {
    const baseFilters: string = 'field_component:article';
    return `${baseFilters}`;
  };

  return (
    <PageContext.Provider
      value={{
        headerIsTransparent: false,
        isHeaderSearchFunctionalityRequired: true,
        showContactBar: blok.show_contact_bar,
      }}
    >
      <AuthorContext.Provider
        value={{
          author: author.name,
        }}
      >
        <div className="flex flex-col h-screen justify-between">
          {header && (
            <StoryblokComponent blok={header.content} key={header._uid} />
          )}
          <main className="mb-auto" {...storyblokEditable(blok)}>
            {heroBloks.map((heroBlok: Blok) => (
              <StoryblokComponent blok={heroBlok} key={heroBlok._uid} />
            ))}

            {breadcrumbs && (
              <BreadcrumbBar
                breadcrumbs={breadcrumbs}
                theme={breadcrumb_theme}
              />
            )}
            {nonHeroBloks.map((nonHeroBlok: Blok) => (
              <StoryblokComponent blok={nonHeroBlok} key={nonHeroBlok._uid} />
            ))}
            {blok.show_contact_bar && contact_bar && (
              <StoryblokComponent
                blok={contact_bar.content}
                key={contact_bar._uid}
              />
            )}
          </main>
          {footer && (
            <StoryblokComponent blok={footer.content} key={footer._uid} />
          )}
        </div>
        <TestingGrid />
      </AuthorContext.Provider>
    </PageContext.Provider>
  );
};

export default memo(AuthorPage);
